// Enhanced motor data with descriptions, relationships, and metadata

export const MOTOR_DATA = [
  {
    id: "1",
    number: 1,
    name: "Bearings",
    color: "#3B82F6",
    failureModes: [
      {
        name: "Bearing Wear",
        description: "Normal wear of bearing rolling elements, races, and cage over operating life.",
        importance: "Worn bearings increase friction, heat, and vibration. Eventually leads to catastrophic failure.",
        approach: "Monitor through vibration analysis. Replace bearings before failure based on condition.",
        severity: "Moderate",
        relatedTests: ["Vibration Analysis", "Ultrasonic Testing"],
        relatedMonitoring: ["Continuous Vibration IoT", "Temperature Sensors"]
      },
      {
        name: "Bearing Seizure",
        description: "Complete locking of bearing due to lubrication failure, overheating, or contamination.",
        importance: "Seized bearing stops motor immediately. Can cause shaft damage and winding failure.",
        approach: "Investigate cause - lubrication, alignment, or loading issues. Replace bearing and address root cause.",
        severity: "Critical",
        relatedTests: ["Vibration Analysis", "Oil Analysis"],
        relatedMonitoring: ["Temperature Sensors"]
      },
      {
        name: "Lubrication Failure",
        description: "Inadequate lubricant quantity, wrong lubricant type, or degraded lubricant properties.",
        importance: "Proper lubrication is critical for bearing life. Failure leads to rapid wear and seizure.",
        approach: "Verify lubrication type and schedule. Sample and analyze grease. Adjust relubrication.",
        severity: "Critical",
        relatedTests: ["Oil Analysis", "Ultrasonic Testing"],
        relatedMonitoring: ["Ultrasonic Monitors"]
      },
      {
        name: "Contamination",
        description: "Foreign particles or moisture entering bearing causing accelerated wear and damage.",
        importance: "Even small particles cause bearing damage. Moisture causes corrosion.",
        approach: "Improve sealing. Keep lubricant clean. Replace damaged bearings.",
        severity: "Moderate",
        relatedTests: ["Oil Analysis"],
        relatedMonitoring: ["Continuous Vibration IoT"]
      },
      {
        name: "Electrical Fluting",
        description: "Bearing damage from electrical currents passing through the bearing.",
        importance: "VFD-driven motors are susceptible. Causes premature bearing failure and noise.",
        approach: "Install shaft grounding or insulated bearings. Use proper VFD cables and grounding.",
        severity: "Moderate",
        relatedTests: ["Vibration Analysis", "Shock Pulse"],
        relatedMonitoring: ["Continuous Vibration IoT"]
      },
      {
        name: "Misalignment",
        description: "Improper alignment between motor and driven equipment causing bearing stress.",
        importance: "Misalignment greatly accelerates bearing wear and increases energy consumption.",
        approach: "Perform precision alignment using laser or dial indicators. Check coupling condition.",
        severity: "Moderate",
        relatedTests: ["Vibration Analysis"],
        relatedMonitoring: ["Continuous Vibration IoT"]
      }
    ],
    tests: [
      {
        name: "Vibration Analysis",
        description: "Measurement and spectral analysis of motor vibration to detect bearing faults.",
        importance: "Primary method for bearing condition assessment. Detects faults early in development.",
        approach: "Measure velocity and acceleration at bearing locations. Analyze spectrum for fault frequencies.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Bearing Wear", "Bearing Seizure", "Electrical Fluting", "Misalignment"]
      },
      {
        name: "Oil Analysis",
        description: "Laboratory analysis of lubricant for wear particles, contamination, and degradation.",
        importance: "Reveals bearing wear and lubrication condition. Complements vibration analysis.",
        approach: "Sample lubricant. Analyze for wear metals, moisture, and viscosity.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Bearing Seizure", "Lubrication Failure", "Contamination"]
      },
      {
        name: "Ultrasonic Testing",
        description: "High-frequency sound measurement to detect early bearing faults and lubrication issues.",
        importance: "Detects problems earlier than vibration analysis. Excellent for slow-speed bearings.",
        approach: "Use ultrasonic instrument at bearing locations. Compare to baseline and between bearings.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Bearing Wear", "Lubrication Failure"]
      },
      {
        name: "Shock Pulse",
        description: "Specialized measurement technique for bearing mechanical condition.",
        importance: "Provides numerical bearing condition index for trending and comparison.",
        approach: "Apply shock pulse sensor at bearings. Record dBm and dBc values. Trend over time.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Electrical Fluting"]
      }
    ],
    monitoring: [
      {
        name: "Continuous Vibration IoT",
        description: "Wireless IoT sensors providing continuous vibration monitoring.",
        importance: "Real-time bearing health status. Enables condition-based maintenance.",
        approach: "Install wireless sensors at each bearing. Connect to cloud analytics platform.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Bearing Wear", "Contamination", "Electrical Fluting", "Misalignment"]
      },
      {
        name: "Temperature Sensors",
        description: "Continuous monitoring of bearing temperature for early fault detection.",
        importance: "Temperature rise indicates bearing problems. Simple and reliable indicator.",
        approach: "Install RTDs or thermocouples at bearings. Set alarm thresholds above normal.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Bearing Wear", "Bearing Seizure"]
      },
      {
        name: "Ultrasonic Monitors",
        description: "Continuous ultrasonic monitoring for lubrication and bearing condition.",
        importance: "Detects lubrication needs before damage occurs. Enables just-in-time lubrication.",
        approach: "Install ultrasonic sensors. Monitor during operation for lubrication scheduling.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Lubrication Failure"]
      }
    ]
  },
  {
    id: "2",
    number: 2,
    name: "Stator Windings",
    color: "#10B981",
    failureModes: [
      {
        name: "Turn-to-Turn Short",
        description: "Short circuit between adjacent turns in the same coil due to insulation failure.",
        importance: "Creates local hot spot that rapidly spreads to complete winding failure.",
        approach: "Detect early with surge testing. May require rewind or replacement.",
        severity: "Critical",
        relatedTests: ["Surge Test", "Motor Circuit Analysis (MCA)"],
        relatedMonitoring: ["RTD Temp Sensors"]
      },
      {
        name: "Phase-to-Phase Short",
        description: "Short circuit between phases causing high circulating currents and heating.",
        importance: "Causes immediate motor trip or damage. Often results from insulation aging.",
        approach: "Perform insulation testing. Requires repair or rewind to correct.",
        severity: "Critical",
        relatedTests: ["Insulation Resistance", "Motor Circuit Analysis (MCA)"],
        relatedMonitoring: ["Stator Ground Fault Relays"]
      },
      {
        name: "Ground Fault",
        description: "Insulation failure allowing current to flow from winding to grounded frame.",
        importance: "Safety hazard. Can cause fire if not detected. Damages winding further.",
        approach: "Locate fault and repair. May require partial or complete rewind.",
        severity: "Critical",
        relatedTests: ["Insulation Resistance", "Polarization Index"],
        relatedMonitoring: ["Stator Ground Fault Relays"]
      },
      {
        name: "Insulation Degradation",
        description: "Gradual deterioration of winding insulation from thermal, electrical, and mechanical stresses.",
        importance: "Reduces safety margin. Eventually leads to short circuit or ground fault.",
        approach: "Monitor through testing and trending. Plan rewind based on condition assessment.",
        severity: "Moderate",
        relatedTests: ["Insulation Resistance", "Polarization Index", "Motor Circuit Analysis (MCA)"],
        relatedMonitoring: ["PD Monitors"]
      },
      {
        name: "Overheating",
        description: "Excessive winding temperature from overload, poor cooling, or high ambient.",
        importance: "Every 10°C rise above design halves insulation life. Cumulative damage.",
        approach: "Verify loading and cooling. Improve ventilation. Reduce load if needed.",
        severity: "Critical",
        relatedTests: ["Motor Circuit Analysis (MCA)"],
        relatedMonitoring: ["RTD Temp Sensors"]
      },
      {
        name: "Partial Discharge",
        description: "Localized discharges in voids or at interfaces in high-voltage motor insulation.",
        importance: "PD progressively erodes insulation. Primary aging mechanism in MV motors.",
        approach: "Test for PD periodically. Monitor if detected. Plan intervention based on severity.",
        severity: "Critical",
        relatedTests: ["Surge Test"],
        relatedMonitoring: ["PD Monitors"]
      }
    ],
    tests: [
      {
        name: "Motor Circuit Analysis (MCA)",
        description: "Comprehensive offline test including resistance, inductance, capacitance, and insulation.",
        importance: "Complete static assessment of motor electrical condition. Industry standard test.",
        approach: "Use MCA instrument to measure all parameters. Compare to baseline and between phases.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Turn-to-Turn Short", "Phase-to-Phase Short", "Insulation Degradation", "Overheating"]
      },
      {
        name: "Insulation Resistance",
        description: "DC measurement of winding insulation resistance to ground.",
        importance: "Basic screening test for gross insulation problems. Simple and essential.",
        approach: "Apply 500V or 1000V DC. Measure IR at 1 minute. Calculate PI at 10 minutes.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Phase-to-Phase Short", "Ground Fault", "Insulation Degradation"]
      },
      {
        name: "Polarization Index",
        description: "Ratio of 10-minute to 1-minute insulation resistance indicating moisture and contamination.",
        importance: "More meaningful than single IR reading. Reveals insulation condition.",
        approach: "Measure IR at 1 and 10 minutes. Calculate ratio. Compare to standards.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Ground Fault", "Insulation Degradation"]
      },
      {
        name: "Surge Test",
        description: "High-voltage impulse test to detect turn-to-turn insulation weakness.",
        importance: "Only way to find turn-to-turn faults that other tests miss.",
        approach: "Apply surge voltage to each phase. Compare waveforms between phases.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Turn-to-Turn Short", "Partial Discharge"]
      }
    ],
    monitoring: [
      {
        name: "RTD Temp Sensors",
        description: "Resistance temperature detectors embedded in stator slots for winding temperature.",
        importance: "Direct measurement of winding temperature. Essential for thermal protection.",
        approach: "Connect RTDs to protection and monitoring. Set alarms based on insulation class.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Turn-to-Turn Short", "Overheating"]
      },
      {
        name: "PD Monitors",
        description: "Online partial discharge monitoring for high-voltage motors.",
        importance: "Continuous assessment of insulation condition. Early warning of problems.",
        approach: "Install capacitive couplers or other PD sensors. Analyze trends and patterns.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Insulation Degradation", "Partial Discharge"]
      },
      {
        name: "Stator Ground Fault Relays",
        description: "Protection relays detecting current flow from stator to ground.",
        importance: "Safety-critical protection. Prevents damage and fire from ground faults.",
        approach: "Install 51G or 59G protection. Set appropriately for motor and system.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Phase-to-Phase Short", "Ground Fault"]
      }
    ]
  },
  {
    id: "3",
    number: 3,
    name: "Rotor Assembly",
    color: "#F59E0B",
    failureModes: [
      {
        name: "Rotor Bar Crack",
        description: "Fatigue cracks developing in squirrel cage rotor bars near the end ring joint.",
        importance: "Cracked bars cause local heating and eventually break. Unbalances rotor.",
        approach: "Detect through motor current signature analysis. Plan rotor replacement.",
        severity: "Moderate",
        relatedTests: ["Current Signature Analysis (CSA)", "Growler Test"],
        relatedMonitoring: ["Current Spectrum Analyzers"]
      },
      {
        name: "Rotor Bar Breakage",
        description: "Complete fracture of one or more rotor bars disrupting current flow.",
        importance: "Broken bars cause severe vibration and heating. Unbalanced magnetic pull.",
        approach: "Remove rotor and repair or replace. Investigate cause of failure.",
        severity: "Critical",
        relatedTests: ["Current Signature Analysis (CSA)", "Single Phase Rotor Test"],
        relatedMonitoring: ["Smart Soft Starters", "Current Spectrum Analyzers"]
      },
      {
        name: "End Ring Fracture",
        description: "Crack or break in the end ring connecting rotor bars.",
        importance: "Interrupts current path for multiple bars. Similar effects to bar breakage.",
        approach: "Requires rotor removal and repair. May need complete rotor replacement.",
        severity: "Critical",
        relatedTests: ["Current Signature Analysis (CSA)", "Growler Test"],
        relatedMonitoring: ["Current Spectrum Analyzers"]
      },
      {
        name: "Rotor Eccentricity",
        description: "Uneven air gap between rotor and stator causing unbalanced magnetic forces.",
        importance: "Creates vibration and bearing stress. Can cause rotor-stator contact.",
        approach: "Check bearing condition and alignment. May need rotor machining or replacement.",
        severity: "Moderate",
        relatedTests: ["Current Signature Analysis (CSA)", "Flux Probe"],
        relatedMonitoring: ["Smart Soft Starters"]
      },
      {
        name: "Core Damage",
        description: "Damage to rotor laminations from overheating, foreign objects, or manufacturing defects.",
        importance: "Damaged core creates hot spots and unbalanced operation.",
        approach: "Inspect rotor core. Repair or replace damaged laminations.",
        severity: "Moderate",
        relatedTests: ["Growler Test", "Flux Probe"],
        relatedMonitoring: []
      },
      {
        name: "Rotor Bow",
        description: "Permanent bending of rotor shaft from overheating or mechanical stress.",
        importance: "Causes severe vibration and bearing damage. Cannot be corrected without machining.",
        approach: "Verify shaft runout. Straighten or replace shaft if bowed.",
        severity: "Critical",
        relatedTests: ["Single Phase Rotor Test"],
        relatedMonitoring: []
      }
    ],
    tests: [
      {
        name: "Current Signature Analysis (CSA)",
        description: "Spectral analysis of motor current to detect rotor bar and eccentricity problems.",
        importance: "Non-invasive online test. Detects rotor problems while motor operates.",
        approach: "Record motor current. Analyze spectrum for sideband frequencies indicating faults.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Rotor Bar Crack", "Rotor Bar Breakage", "End Ring Fracture", "Rotor Eccentricity"]
      },
      {
        name: "Growler Test",
        description: "Electromagnetic test of removed rotor to locate broken bars and ring faults.",
        importance: "Definitive test for rotor bar condition. Requires rotor removal.",
        approach: "Place rotor on growler. Use steel blade to detect broken bars by vibration.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Rotor Bar Crack", "End Ring Fracture", "Core Damage"]
      },
      {
        name: "Single Phase Rotor Test",
        description: "Rotor current test using single-phase power to detect asymmetry.",
        importance: "Simple test revealing rotor problems. Requires rotor removal.",
        approach: "Apply single-phase voltage. Slowly rotate rotor and observe current variation.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Rotor Bar Breakage", "Rotor Bow"]
      },
      {
        name: "Flux Probe",
        description: "External magnetic field measurement around motor to detect rotor faults.",
        importance: "Non-invasive test detecting rotor problems. Can be done during operation.",
        approach: "Place flux probe around motor. Analyze signal for rotor fault signatures.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Rotor Eccentricity", "Core Damage"]
      }
    ],
    monitoring: [
      {
        name: "Smart Soft Starters",
        description: "Soft starters with built-in motor health monitoring including current analysis.",
        importance: "Provides starting protection and continuous monitoring in one device.",
        approach: "Use soft starter current monitoring features. Configure alarms for rotor faults.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Rotor Bar Breakage", "Rotor Eccentricity"]
      },
      {
        name: "Current Spectrum Analyzers",
        description: "Dedicated devices for continuous motor current signature analysis.",
        importance: "Ongoing monitoring of rotor condition. Early detection of developing faults.",
        approach: "Install current sensors. Analyze spectrum continuously for fault signatures.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Rotor Bar Crack", "Rotor Bar Breakage", "End Ring Fracture"]
      }
    ]
  },
  {
    id: "4",
    number: 4,
    name: "Cooling System",
    color: "#EF4444",
    failureModes: [
      {
        name: "Fan Damage",
        description: "Broken, cracked, or missing fan blades reducing cooling airflow.",
        importance: "Reduced cooling leads to overheating and accelerated aging.",
        approach: "Inspect fan during maintenance. Replace damaged fans immediately.",
        severity: "Moderate",
        relatedTests: ["Airflow Measurement", "Thermal Imaging"],
        relatedMonitoring: ["Airflow Sensors"]
      },
      {
        name: "Air Passage Blockage",
        description: "Obstruction of cooling air paths by debris, dust, or damage.",
        importance: "Blocked passages dramatically reduce cooling effectiveness.",
        approach: "Clean air passages thoroughly. Improve filtration if contamination is ongoing.",
        severity: "Moderate",
        relatedTests: ["Airflow Measurement", "Thermal Imaging"],
        relatedMonitoring: ["Temperature Differential"]
      },
      {
        name: "Heat Exchanger Fouling",
        description: "Buildup of deposits on heat exchanger surfaces reducing heat transfer.",
        importance: "Fouled exchangers cannot remove heat effectively. Motor runs hotter.",
        approach: "Clean heat exchanger tubes and surfaces. Improve water treatment if needed.",
        severity: "Moderate",
        relatedTests: ["Thermal Imaging", "Differential Pressure Test"],
        relatedMonitoring: ["Temperature Differential"]
      },
      {
        name: "Cooling Water Issues",
        description: "Problems with cooling water supply including flow, temperature, or quality.",
        importance: "Water-cooled motors depend on adequate cooling water. Problems cause overheating.",
        approach: "Verify water flow and temperature. Check for fouling or blockage.",
        severity: "Critical",
        relatedTests: ["Thermal Imaging"],
        relatedMonitoring: ["Temperature Differential"]
      },
      {
        name: "Air Filter Clogging",
        description: "Intake air filters becoming restricted with accumulated dust and debris.",
        importance: "Clogged filters reduce airflow and increase motor temperature.",
        approach: "Replace or clean filters regularly. Use correct filter type for environment.",
        severity: "Low",
        relatedTests: ["Differential Pressure Test"],
        relatedMonitoring: ["Filter DP Monitors"]
      }
    ],
    tests: [
      {
        name: "Airflow Measurement",
        description: "Measurement of cooling air velocity and volume through motor.",
        importance: "Verifies adequate cooling. Detects fan problems and blockages.",
        approach: "Use anemometer to measure air velocity at inlet and outlet. Calculate flow rate.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Fan Damage", "Air Passage Blockage"]
      },
      {
        name: "Thermal Imaging",
        description: "Infrared survey of motor surfaces to identify hot spots and cooling problems.",
        importance: "Visual identification of cooling issues and abnormal temperatures.",
        approach: "Scan motor during operation. Compare to baseline and identify hot areas.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Fan Damage", "Air Passage Blockage", "Heat Exchanger Fouling", "Cooling Water Issues"]
      },
      {
        name: "Differential Pressure Test",
        description: "Measurement of pressure drop across filters and heat exchangers.",
        importance: "High differential indicates clogging or fouling requiring attention.",
        approach: "Install temporary or permanent DP gauges. Compare to clean equipment baseline.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Heat Exchanger Fouling", "Air Filter Clogging"]
      }
    ],
    monitoring: [
      {
        name: "Temperature Differential",
        description: "Monitoring of temperature difference between cooling inlet and outlet.",
        importance: "Rising differential indicates reduced cooling effectiveness.",
        approach: "Install temperature sensors at cooling inlet and outlet. Trend differential.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Air Passage Blockage", "Heat Exchanger Fouling", "Cooling Water Issues"]
      },
      {
        name: "Airflow Sensors",
        description: "Continuous monitoring of cooling airflow through motor.",
        importance: "Immediate detection of fan failure or blockage.",
        approach: "Install airflow switch or sensor. Alarm on low flow condition.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Fan Damage"]
      },
      {
        name: "Filter DP Monitors",
        description: "Continuous monitoring of filter differential pressure.",
        importance: "Indicates when filter replacement is needed before flow is impacted.",
        approach: "Install DP gauge across filter. Set alarm at replacement threshold.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Air Filter Clogging"]
      }
    ]
  },
  {
    id: "5",
    number: 5,
    name: "Shaft & Coupling",
    color: "#8B5CF6",
    failureModes: [
      {
        name: "Shaft Fatigue",
        description: "Fatigue cracking in shaft from cyclic stresses, often at stress concentrations.",
        importance: "Shaft failure is catastrophic. Can destroy motor and driven equipment.",
        approach: "Inspect shaft for cracks during major maintenance. Monitor vibration for changes.",
        severity: "Critical",
        relatedTests: ["Runout Measurement", "Vibration Phase Analysis"],
        relatedMonitoring: ["Vibration Trending"]
      },
      {
        name: "Coupling Wear",
        description: "Wear of coupling elements including elastomeric inserts, gear teeth, or grid springs.",
        importance: "Worn coupling causes vibration and misalignment. Can fail suddenly.",
        approach: "Inspect coupling during maintenance. Replace worn elements before failure.",
        severity: "Moderate",
        relatedTests: ["Vibration Phase Analysis"],
        relatedMonitoring: ["Vibration Trending"]
      },
      {
        name: "Keyway Damage",
        description: "Wear, cracking, or deformation of shaft keyway from torque transmission.",
        importance: "Damaged keyway can allow coupling slip or sudden failure.",
        approach: "Inspect keyway during coupling removal. Repair or replace shaft if damaged.",
        severity: "Moderate",
        relatedTests: ["Runout Measurement"],
        relatedMonitoring: []
      },
      {
        name: "Alignment Issues",
        description: "Angular or offset misalignment between motor and driven equipment shafts.",
        importance: "Misalignment causes vibration, bearing wear, and coupling damage.",
        approach: "Perform precision alignment using laser or dial methods. Verify under operating conditions.",
        severity: "Moderate",
        relatedTests: ["Laser Alignment", "Vibration Phase Analysis"],
        relatedMonitoring: ["Vibration Trending"]
      },
      {
        name: "Coupling Imbalance",
        description: "Uneven mass distribution in coupling causing vibration.",
        importance: "Imbalance causes cyclic forces accelerating bearing and coupling wear.",
        approach: "Balance coupling or replace with balanced unit. Check for missing parts.",
        severity: "Low",
        relatedTests: ["Vibration Phase Analysis"],
        relatedMonitoring: ["Vibration Trending"]
      },
      {
        name: "Shaft Seal Failure",
        description: "Failure of shaft seals allowing lubricant leakage or contamination ingress.",
        importance: "Leaking seals cause bearing contamination and environmental issues.",
        approach: "Replace worn seals. Use correct seal type for application.",
        severity: "Low",
        relatedTests: [],
        relatedMonitoring: ["Oil Leak Detection"]
      }
    ],
    tests: [
      {
        name: "Laser Alignment",
        description: "Precision alignment measurement using laser alignment tools.",
        importance: "Gold standard for alignment verification. Essential for reliable operation.",
        approach: "Mount laser tools on shafts. Measure and correct angular and offset misalignment.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Alignment Issues"]
      },
      {
        name: "Runout Measurement",
        description: "Measurement of shaft radial and axial runout to detect bending or damage.",
        importance: "Excessive runout indicates shaft problems requiring correction.",
        approach: "Use dial indicators at coupling hub and along shaft. Compare to tolerances.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Shaft Fatigue", "Keyway Damage"]
      },
      {
        name: "Vibration Phase Analysis",
        description: "Vibration measurement with phase reference to diagnose specific problems.",
        importance: "Phase data distinguishes between imbalance, misalignment, and other faults.",
        approach: "Measure vibration with tachometer reference. Analyze phase relationships.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Shaft Fatigue", "Coupling Wear", "Alignment Issues", "Coupling Imbalance"]
      }
    ],
    monitoring: [
      {
        name: "Vibration Trending",
        description: "Long-term tracking of vibration levels to detect developing problems.",
        importance: "Gradual changes indicate wear or developing faults. Enables planning.",
        approach: "Record vibration regularly. Plot trends and set alarm thresholds.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Shaft Fatigue", "Coupling Wear", "Alignment Issues", "Coupling Imbalance"]
      },
      {
        name: "Oil Leak Detection",
        description: "Visual or sensor-based detection of lubricant leakage from seals.",
        importance: "Early leak detection prevents contamination and environmental issues.",
        approach: "Visual inspection or install leak sensors. Address leaks promptly.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Shaft Seal Failure"]
      }
    ]
  }
];

export const getSimpleMotorComponents = () => {
  return MOTOR_DATA.map(comp => ({
    id: comp.id,
    number: comp.number,
    name: comp.name,
    color: comp.color,
    failureModes: comp.failureModes.map(f => f.name),
    tests: comp.tests.map(t => t.name),
    monitoring: comp.monitoring.map(m => m.name)
  }));
};
